import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Close, Send } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import useSendReorderEmail from "../../../queries/routes/useSendReorderEmail";
import { EmailTemplate } from "../../../types/api/EmailTemplate";
import { Get } from "../../../queries/utils/api";
import { useAuth0 } from "@auth0/auth0-react";

interface SendReorderEmailDialogProps {
  open: boolean;
  onClose: () => void;
  customer: {
    organization: {
      id: string;
      name: string;
      email?: string;
    };
    last_order?: string;
    count: number;
  } | null;
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

export default function SendReorderEmailDialog({
  open,
  onClose,
  customer,
  onSuccess,
  onError,
}: SendReorderEmailDialogProps) {
  const { org_id } = useParams();
  const { getAccessTokenSilently } = useAuth0();
  const [emailData, setEmailData] = useState({
    to: "",
    subject: "",
    body: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendReorderEmail = useSendReorderEmail(org_id!);

  // Quill editor configuration
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'font': [] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image', 'color', 'background',
    'align'
  ];

  // Fetch email content when dialog opens
  useEffect(() => {
    if (open && customer?.organization?.id && org_id) {
      const fetchEmailContent = async () => {
        setIsLoading(true);
        setError(null);
        try {
          const emailContent = await Get<EmailTemplate>(
            `/api/order/organization/${org_id}/reorder-email/${customer.organization.id}`,
            await getAccessTokenSilently()
          );

          console.log("Fetched email content:", emailContent);

          setEmailData({
            to: emailContent.to || "",
            subject: emailContent.subject || "",
            body: emailContent.body || "",
          });
        } catch (err) {
          console.error("Failed to fetch email content:", err);
          setError("Failed to load email template");
        } finally {
          setIsLoading(false);
        }
      };

      fetchEmailContent();
    }
  }, [open, customer?.organization?.id, org_id, getAccessTokenSilently]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setEmailData({ to: "", subject: "", body: "" });
      setError(null);
    }
  }, [open]);

  const handleSendEmail = async () => {
    try {
      await sendReorderEmail.mutateAsync({
        to_email: emailData.to,
        subject: emailData.subject,
        body: emailData.body,
        recipient_organization_id: customer?.organization?.id,
      });
      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    } catch (error) {
      console.error("Failed to send email:", error);
      if (onError) {
        onError(error);
      }
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Send Reorder Email</Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Box sx={{ mb: 2, p: 2, bgcolor: '#ffebee', borderRadius: 1, border: '1px solid #f44336' }}>
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            </Box>
          )}

          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TextField
                label="To:"
                fullWidth
                value={emailData.to}
                onChange={(e) =>
                  setEmailData({ ...emailData, to: e.target.value })
                }
                sx={{ mb: 2 }}
              />
              <TextField
                label="Subject:"
                fullWidth
                value={emailData.subject}
                onChange={(e) =>
                  setEmailData({ ...emailData, subject: e.target.value })
                }
                sx={{ mb: 2 }}
              />
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Email Body:
                </Typography>
                <Box sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  '& .ql-editor': {
                    minHeight: '200px',
                    maxHeight: '400px',
                    overflowY: 'auto'
                  },
                  '& .ql-toolbar': {
                    borderTop: 'none',
                    borderLeft: 'none',
                    borderRight: 'none',
                    borderBottom: '1px solid #e0e0e0'
                  },
                  '& .ql-container': {
                    border: 'none'
                  }
                }}>
                  <ReactQuill
                    theme="snow"
                    value={emailData.body}
                    onChange={(content) =>
                      setEmailData({ ...emailData, body: content })
                    }
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Compose your email here..."
                  />
                </Box>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSendEmail}
          variant="contained"
          startIcon={sendReorderEmail.isLoading ? <CircularProgress size={16} /> : <Send />}
          disabled={isLoading || sendReorderEmail.isLoading}
          sx={{
            backgroundColor: "#A19AED",
            color: "white",
            "&:hover": {
              backgroundColor: "#8B7DD8",
            },
          }}
        >
          {sendReorderEmail.isLoading ? "Sending..." : "Send Email"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
